/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DoCheck, Inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './advertiser-information-page.component.html';

import {orderListCommons} from '../order/order-commons';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {creativeSetListCommons} from '../creative-set/creative-set-commons';
import {folioListCommons} from '../folio/folio-commons';
import {creativeListCommons} from '../creative/creative-commons';
import {advertiserNavigationParams} from '../../components/util/navigation-context';
import {AdnResourceListService} from '../common/controller/adn-resource-list.service';
import {LineItemListSetupService} from '../line-item/line-item-list-setup.service';
import {OrderResource} from '../../components/api/resources/resource/order.resource';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {CreativeSetResource} from '../../components/api/resources/resource/creative-set.resource';
import {FolioResource} from '../../components/api/resources/resource/folio.resource';
import {LineItemResource} from '../../components/api/resources/resource/line-item.resource';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {StateService} from '@uirouter/core';


type AdvertiserInformationTab = 'orders' | 'lineItems' | 'folios' | 'creativeSets' | 'creatives';

function emptyCountList(): {totalCountText: string} {
  // Seed list count state before the list helper is attached.
  return {totalCountText: ''};
}

@Component({
  standalone: false,
  selector: 'adn-advertiser-information-page',
  template
})
export class AdvertiserInformationPageComponent implements DoCheck, OnChanges, OnDestroy {
  @Input() model: any;
  private _modelCommsDirect: any;
  private _isNewDirect: boolean | undefined;
  private _readOnlyDirect: boolean | undefined;

  @Input()
  set modelComms(value: any) {
    this._modelCommsDirect = value;
  }

  get modelComms(): any {
    return this._modelCommsDirect;
  }

  @Input()
  set isNew(value: boolean) {
    this._isNewDirect = value;
  }

  get isNew(): boolean {
    return this._isNewDirect;
  }

  @Input()
  set readOnly(value: boolean) {
    this._readOnlyDirect = value;
  }

  get readOnly(): boolean {
    return this._readOnlyDirect;
  }

  effectiveIsNew = false;
  activeTab: AdvertiserInformationTab = 'orders';

  showOrders = false;
  showCreativeSets = false;
  showFolios = false;
  showCreatives = false;

  orders: any = emptyCountList();
  lineItems: any = emptyCountList();
  folios: any = emptyCountList();
  creativeSets: any = emptyCountList();
  creatives: any = emptyCountList();

  private lastModelId: string | undefined;
  private subscribedModelComms: any;
  private destroyed = false;

  constructor(
    @Inject(FolioResource) private readonly Folio: any,
    @Inject(CreativeSetResource) private readonly CreativeSet: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly translate: TranslateService,
    private readonly $state: StateService,
    @Inject(OrderResource) private readonly Order: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(LineItemResource) private readonly LineItem: any,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly ngZone: NgZone,
    private readonly listService: AdnResourceListService,
    private readonly lineItemListSetup: LineItemListSetupService
  ) {
    const sectionPerms = LocalUserPermissions.getSectionPerms();
    this.showOrders = !!sectionPerms.orders;
    this.showCreativeSets = !!sectionPerms.creativeSets && LocalNetworkInfo.obtain().getInfo('creativeSetCount') > 0;
    this.showFolios = !!sectionPerms.folios && LocalNetworkInfo.obtain().getInfo('folioCount') > 0;
    this.showCreatives = !!sectionPerms.creatives;
    if (this.showOrders) {
      this.activeTab = 'orders';
    } else if (this.showCreatives) {
      this.activeTab = 'lineItems';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      const wasNew = changes.isNew.previousValue !== undefined && !!changes.isNew.previousValue;
      this.effectiveIsNew = !!this.isNew;
      if (wasNew && !this.effectiveIsNew) {
        this.initLists();
      }
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId === this.lastModelId) {
      return;
    }
    this.lastModelId = modelId;
    // Macrotask — list fill setting totalCountText mid-CD is NG0100 on tab badges.
    setTimeout(() => {
      if (this.destroyed || this.lastModelId !== modelId) {
        return;
      }
      this.ngZone.run(() => this.initLists());
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  selectTab(tab: AdvertiserInformationTab): void {
    this.activeTab = tab;
  }

  newOrder(): void {
    this.$state.go('app.adv.orders.new', advertiserNavigationParams(this.model));
  }

  newCreative(): void {
    this.$state.go('app.adv.creatives.new', advertiserNavigationParams(this.model));
  }

  newBulkCreative(): void {
    this.$state.go('app.adv.creatives.bulkupload', advertiserNavigationParams(this.model));
  }

  private initLists(): void {
    if (!this.model?.id || this.effectiveIsNew) {
      this.orders = emptyCountList();
      this.lineItems = emptyCountList();
      this.folios = emptyCountList();
      this.creativeSets = emptyCountList();
      this.creatives = emptyCountList();
      return;
    }

    this.orders = emptyCountList();
    this.listService.createBasicList(this.orders, this.Order, 'fullOrdersForAdvertiser', {
      moreParams: {advertiser: this.model.id},
      baseTable: 'order'
    });
    orderListCommons(this.orders, false, this.LocalNetworkProfile, this.LocalUserPermissions);
    this.orders.applyLimitedFilter();

    this.lineItems = this.lineItemListSetup.createEmbeddedList({
      listId: 'fullLineItemsForAdvertiser',
      moreParams: {advertiser: this.model.id},
      advertiserId: this.model.id,
      blockTeams: true
    });
    if (this.lineItems.totalCountText === undefined || this.lineItems.totalCountText === null) {
      this.lineItems.totalCountText = '';
    }

    this.folios = emptyCountList();
    if (LocalNetworkInfo.obtain().getInfo('folioCount') > 0) {
      this.listService.createBasicList(this.folios, this.Folio, 'fullFoliosForAdvertiser', {
        moreParams: {advertiser: this.model.id},
        baseTable: 'folio',
        afterFill: () => {
          folioListCommons(this.folios, true, this.LocalNetworkProfile, this.LocalUserPermissions);
        }
      });
      folioListCommons(this.folios, false, this.LocalNetworkProfile, this.LocalUserPermissions);
    }

    this.creativeSets = emptyCountList();
    if (LocalNetworkInfo.obtain().getInfo('creativeSetCount') > 0) {
      this.listService.createBasicList(this.creativeSets, this.CreativeSet, 'fullCreativeSetsForAdvertiser', {
        moreParams: {advertiser: this.model.id},
        baseTable: 'creativeSet',
        afterFill: () => {
          creativeSetListCommons(this.creativeSets, true, this.LocalNetworkProfile, this.adnTimeShifter);
        }
      });
      creativeSetListCommons(this.creativeSets, false, this.LocalNetworkProfile, this.adnTimeShifter);
    }

    this.creatives = emptyCountList();
    this.listService.createBasicList(this.creatives, this.Creative, 'fullCreativesForAdvertiser', {
      moreParams: {advertiser: this.model.id},
      baseTable: 'creative',
      afterFill: () => {
        creativeListCommons(this.creatives, true, this.LocalNetworkProfile, this.$state);
      }
    });
    creativeListCommons(this.creatives, false, this.LocalNetworkProfile, this.$state);
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any, isNewFromComms: boolean) => {
      if (type) {
        return;
      }
      this.ngZone.run(() => {
        const wasNew = this.effectiveIsNew;
        if (isNewFromComms === true || isNewFromComms === false) {
          this.effectiveIsNew = isNewFromComms;
        } else if (data?.name) {
          this.effectiveIsNew = false;
        }
        if (data?.name !== undefined && this.model) {
          this.model.name = data.name;
        }
        if (wasNew && !this.effectiveIsNew) {
          this.initLists();
        } else if (!this.effectiveIsNew) {
          this.refreshEmbeddedLists();
        }
      });
    });
  }

  private refreshEmbeddedLists(): void {
    _.each([this.orders, this.lineItems, this.folios, this.creativeSets, this.creatives], (list: any) => {
      if (_.isFunction(list?.makeQuery)) {
        list.makeQuery();
      }
    });
  }
}
